import React from 'react';
import {
  AlertTriangleIcon,
  ListChecksIcon,
  UserIcon,
  UsersIcon,
} from 'lucide-react';
import DialogBody from '../../../components/customLayout/dialogBody';

interface MissingFieldsNoticeProps {
  /** No selection made yet for at least one unlocked league. */
  needsTeamSelection?: boolean;
  /** "Meldung durch" is still empty. */
  needsReporter?: boolean;
  /** League labels whose Spielgemeinschaft club name is still missing. */
  missingSpielgemeinschaftClubs?: string[];
  /** Shown at the bottom, e.g. which step the club has to go back to. */
  footnote?: React.ReactNode;
}

const Requirement: React.FC<{
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}> = ({ icon, title, children }) => (
  <li className="flex items-start gap-3 rounded-lg border border-gray-200 bg-gray-50 p-3">
    <div className="w-7 h-7 shrink-0 bg-amber-100 rounded-md flex items-center justify-center">
      {icon}
    </div>
    <div className="min-w-0">
      <div className="text-sm font-semibold text-gray-900">{title}</div>
      <div className="text-xs text-gray-600 mt-1 leading-relaxed">
        {children}
      </div>
    </div>
  </li>
);

/**
 * The single place that phrases "you still have to fill this in" for the team
 * registration, so the Team tab guard and the final confirmation step cannot
 * word the same problem differently.
 */
const MissingFieldsNotice: React.FC<MissingFieldsNoticeProps> = ({
  needsTeamSelection = false,
  needsReporter = false,
  missingSpielgemeinschaftClubs = [],
  footnote,
}) => (
  <DialogBody className="py-2">
    <div className="text-center">
      <div className="w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-3">
        <AlertTriangleIcon className="w-7 h-7 text-amber-600" />
      </div>
      <div className="text-lg font-semibold text-gray-900">
        Pflichtangaben fehlen
      </div>
      <p className="text-sm text-gray-500 mt-1">
        Bitte ergänzen Sie die folgenden Angaben, um fortzufahren.
      </p>
    </div>

    <ul className="mt-4 space-y-2 text-left">
      {needsTeamSelection && (
        <Requirement
          icon={<ListChecksIcon className="w-4 h-4 text-amber-700" />}
          title="Anzahl der Mannschaften"
        >
          Bitte treffen Sie für jede freigeschaltete Liga eine Auswahl –
          entweder die Anzahl der Mannschaften oder{' '}
          <span className="font-medium text-gray-900">
            „Keine Mannschaft melden“
          </span>
          .
        </Requirement>
      )}

      {needsReporter && (
        <Requirement
          icon={<UserIcon className="w-4 h-4 text-amber-700" />}
          title="Meldung durch"
        >
          Bitte geben Sie Vor- und Nachnamen der meldenden Person an.
        </Requirement>
      )}

      {missingSpielgemeinschaftClubs.length > 0 && (
        <Requirement
          icon={<UsersIcon className="w-4 h-4 text-amber-700" />}
          title="Spielgemeinschaft - Clubname"
        >
          Bitte tragen Sie den Golfclub ein, mit dem Sie die Spielgemeinschaft
          eingehen – für{' '}
          {missingSpielgemeinschaftClubs.length === 1
            ? 'diese Liga'
            : 'diese Ligen'}
          :
          <ul className="mt-2 space-y-1">
            {missingSpielgemeinschaftClubs.map((label) => (
              <li key={label} className="flex items-start gap-2">
                <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500" />
                <span className="font-semibold text-gray-900 break-words">
                  {label}
                </span>
              </li>
            ))}
          </ul>
        </Requirement>
      )}
    </ul>

    {footnote && (
      <p className="mt-3 text-xs text-gray-500 text-center">{footnote}</p>
    )}
  </DialogBody>
);

export default MissingFieldsNotice;
